import React from 'react';
import { CheckBadgeIcon } from '@heroicons/react/20/solid';
import type { BuildStat } from '../../types/buildExpansion';

/**
 * Quiet horizontal stats strip for the build page. Shows up to 8 stats;
 * 2-column wrap on phones, single row from md. Verified stats carry a small
 * check with screen-reader text. Identity data, not engagement chrome.
 */

const MAX_STATS = 8;

export interface BuildStatsStripProps {
  stats: BuildStat[];
  className?: string;
}

export const BuildStatsStrip: React.FC<BuildStatsStripProps> = ({ stats, className = '' }) => {
  const shown = stats.filter((s) => s.value != null && String(s.value).trim() !== '').slice(0, MAX_STATS);
  if (shown.length === 0) return null;

  return (
    <ul
      className={`stat-ledger grid grid-cols-2 md:flex md:flex-wrap list-none ${className}`}
      aria-label="Build stats"
    >
      {shown.map((stat, index) => (
        <li
          key={stat.key}
          className={[
            'px-3 py-4 sm:py-5 text-center md:flex-1 md:min-w-[110px] border-white/[0.06]',
            index % 2 === 1 ? 'border-l md:border-l' : index === 0 ? '' : 'md:border-l',
            index >= 2 ? 'border-t md:border-t-0' : '',
          ].join(' ')}
        >
          <div className="flex items-baseline justify-center gap-1.5">
            <span className="font-display text-2xl sm:text-[1.75rem] font-semibold tracking-tight text-white tabular-nums leading-none">
              {stat.value}
            </span>
            {stat.verified && (
              <span className="inline-flex self-center" title="Verified">
                <CheckBadgeIcon className="w-4 h-4 text-emerald-400 shrink-0" aria-hidden />
                <span className="sr-only">Verified</span>
              </span>
            )}
          </div>
          <div className="spec-label mt-2">{stat.label}</div>
          {stat.detail && <div className="text-[11px] text-gray-500 mt-1">{stat.detail}</div>}
        </li>
      ))}
    </ul>
  );
};

export default BuildStatsStrip;
